import { apiGetEventChartData } from "@/api/api";
import {
    apiGetCryptoDetail,
    apiGetCryptoEventGuesses,
    apiGetCryptoPrice5sRecent,
    PageCryptoEventGuessRequest,
} from "@/api/event";
import { EventCategory } from "@/domain/event";
import { CryptoGuess } from "@/event-core/types";
import { get } from "@/utils/utils";
export async function fetchCryptoEventDetail( eventId: string ) {
    const [ ok, res ] = await apiGetCryptoDetail( eventId );
    if ( ok ) {
        const guessList = ( res.data.guesses || [] ).sort( ( a: any, b: any ) => {
            const timeA = get( a, "scheduleStartTime", "" );
            const timeB = get( b, "scheduleStartTime", "" );
            return new Date( timeA ).getTime() - new Date( timeB ).getTime();
        } );
        return {
            eventData: {
                ...res.data.event,
                eventCategory: EventCategory.Crypto,
                eventTitle: res.data.event.title,
                eventImageUrl: res.data.event.imageUrl,
            },
            guessList,
        };
    }
    return { eventData: {}, guessList: [] };
}

export async function fetchCryptoGuessPage(
    params: PageCryptoEventGuessRequest,
): Promise<{ guessList: CryptoGuess[]; hasMore: boolean }> {
    params.size = params.size || 20;
    const [ ok, res ] = await apiGetCryptoEventGuesses( params );
    if ( ok ) {
        const guessList: CryptoGuess[] = res.data || [];
        const hasMore = guessList.length >= params.size;
        return { guessList, hasMore };
    }
    return { guessList: [], hasMore: false };
}
// apiGetEventChartData
type ChartDataPoint = { timestamp: number; timeStr: string; chance: number; yesPrice: string; noPrice: string };
type ChartDataList = {
    dataPoints?: { timestamp: number; timeStr: string; chance: number; yesPrice: string; noPrice: string }[];
    guessNo: string;
};

type CryptoRecentPriceRawPoint = { tickTs: number | string; price: number | string };

export type CryptoPricePoint = { time: number; value: number };

function normalizeCryptoTimestamp( value: number | string ) {
    const timestamp = Number( value );
    if ( !Number.isFinite( timestamp ) || timestamp <= 0 ) return undefined;
    if ( timestamp > 100000000000000 ) return Math.floor( timestamp / 1000000 );
    if ( timestamp > 100000000000 ) return Math.floor( timestamp / 1000 );
    return Math.floor( timestamp );
}

function normalizeCryptoRecentPricePoint( point: CryptoRecentPriceRawPoint ) {
    const time = normalizeCryptoTimestamp( point.tickTs );
    const value = Number( point.price );

    if ( time === undefined || !Number.isFinite( value ) ) return undefined;
    return { time, value };
}

function sortAndDedupeCryptoPricePoints( points: CryptoPricePoint[] ) {
    return points.sort( ( a, b ) => a.time - b.time ).filter( ( point, index, sortedPoints ) =>
        index === sortedPoints.length - 1 || point.time !== sortedPoints[index + 1].time
    );
}

export async function fetchCryptoPrice5sRecent(
    symbolId: string,
    thirdType: string | number | undefined,
): Promise<CryptoPricePoint[]> {
    if ( !symbolId ) return [];

    const [ isOk, res ] = await apiGetCryptoPrice5sRecent( { symbolId, thirdType } );
    if ( !isOk ) return [];

    if ( !Array.isArray( res.data ) ) return [];

    const points = ( res.data as CryptoRecentPriceRawPoint[] ).map( ( point ) =>
        normalizeCryptoRecentPricePoint( point )
    ).filter( ( point ): point is CryptoPricePoint => point !== undefined );

    return sortAndDedupeCryptoPricePoints( points );
}

// 获取chart的数据
export async function fetchGetEventChartData( eventNo, guessNoList: CryptoGuess[] ): Promise<ChartDataPoint[]> {
    const [ isOk, res ] = await apiGetEventChartData( {
        eventNo,
        timeType: 1,
        guessNoList: guessNoList.map( ( item ) => item.guessNo ),
    } );
    if ( isOk ) {
        const chartDataList = res.data.chartDataList as ChartDataList[];

        // 处理每个 guessNo 对应的数据
        const allDataPoints: ChartDataPoint[] = [];

        guessNoList.forEach( ( guess, index ) => {
            const chartData = chartDataList.find( item => item.guessNo === guess.guessNo );
            if ( !chartData || !chartData.dataPoints ) return;

            // 获取时间范围（毫秒转秒）
            const startTimeS = ( guess.scheduleStartTime || 0 ) / 1000;
            const endTimeS = ( guess.scheduleEndTime || 0 ) / 1000;

            const prevGuess = index > 0 ? guessNoList[index - 1] : undefined;
            const prevEndTimeS = prevGuess ? ( prevGuess.scheduleEndTime || 0 ) / 1000 : undefined;
            const lowerBound = prevEndTimeS !== undefined ? Math.max( startTimeS, prevEndTimeS ) : startTimeS;
            const isLeftOpen = prevEndTimeS !== undefined && prevEndTimeS >= startTimeS;

            // 过滤在时间范围内的 dataPoints (左开右闭)
            const filteredPoints = chartData.dataPoints.filter( point => {
                const lowerOk = isLeftOpen ? point.timestamp > lowerBound : point.timestamp >= lowerBound;
                return lowerOk && point.timestamp <= endTimeS;
            } );

            // 拼接到结果数组
            allDataPoints.push( ...filteredPoints );
        } );

        return allDataPoints.sort( ( a, b ) => a.timestamp - b.timestamp );
    }
    return [];
}
